import { Menu, Dropdown, Typography, Tag } from 'antd';
import {
  AlertOutlined,
  HistoryOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  AuditOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { VibrationIcon, ColdStorageMenuIcon } from '../icons/HvacIcons';
import useAuthStore from '../../stores/authStore';

import { hasPageAccess } from '../../constants/permissions';

const { Text } = Typography;

const COLLAPSE_KEY = '__collapse__';

// 馬修選單：冷藏系統 / 趨勢報表 / AI振動量測 / 能源管理 / 警報管理 / 報表管理 / 稽核日誌 / 系統功能
// 報表管理（/report）：每日 01:00 自動產生昨日報表（CSV/PDF）+ Email；後端持續運作，於警報管理與稽核日誌之間掛回。
// 稽核日誌（電子記錄 21 CFR Part 11）保留：合規必要；可見度由「頁面存取權限」矩陣控制
// （預設 /audit=admin 以上，見 DEFAULT_PAGE_MIN_LEVEL；經 visibleItems 的 hasPageAccess 過濾）
const menuItems = [
  { key: '/monitoring', icon: <ColdStorageMenuIcon />, labelKey: 'nav.monitoring' },
  { key: '/history', icon: <HistoryOutlined />, labelKey: 'nav.history' },
  { key: '/vibration', icon: <VibrationIcon />, labelKey: 'nav.vibration' },
  { key: '/energy', icon: <ThunderboltOutlined />, labelKey: 'nav.energy' },
  { key: '/alarm', icon: <AlertOutlined />, labelKey: 'nav.alarm' },
  { key: '/report', icon: <FileTextOutlined />, labelKey: 'nav.report' },
  { key: '/audit', icon: <AuditOutlined />, labelKey: 'nav.audit' },
  { key: '/system', icon: <SettingOutlined />, labelKey: 'nav.system' },
  { key: '/info', icon: <InfoCircleOutlined />, labelKey: 'nav.info' },
];

interface Props {
  collapsed?: boolean;
  onCollapse?: () => void;
  onClose?: () => void; // 手機 Drawer 模式：導航後關閉
}

export default function AppSidebar({ collapsed, onCollapse, onClose }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pagePermissions = useAuthStore((s) => s.pagePermissions);
  const alarmRoleFilter = useAuthStore((s) => s.alarmRoleFilter);
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded);

  const itemStyle = { paddingInlineStart: 16, paddingInlineEnd: 16, justifyContent: 'center' };

  // 依權限過濾側邊欄（權限未載入時以 DEFAULT_PAGE_MIN_LEVEL 預設值過濾，不隱藏全部選單）
  const visibleItems = menuItems.filter((item) => {
    if (!user) return false;
    return hasPageAccess(item.key, user.role_level, user.role, pagePermissions, alarmRoleFilter);
  });

  const items: any[] = visibleItems.map((item) => ({
    key: item.key,
    icon: item.icon,
    style: itemStyle,
    title: t(item.labelKey),   // 收合時的 hover tooltip 用純文字，避免沿用 5em label span 而換行
    label: (
      <span style={{ display: 'inline-block', width: '5em', textAlignLast: 'justify' }}>
        {t(item.labelKey)}
      </span>
    ),
  }));

  if (onCollapse) {
    items.push({
      key: COLLAPSE_KEY,
      icon: collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />,
      style: itemStyle,
      title: collapsed ? '展開側邊' : '收合側邊',
      label: (
        <span style={{ display: 'inline-block', width: '5em', textAlignLast: 'justify' }}>
          {collapsed ? '展開側邊' : '收合側邊'}
        </span>
      ),
    });
  }

  // 使用者頭像下拉選單（Claude 風格）
  const initial = user?.username?.charAt(0)?.toUpperCase() || 'U';
  const userMenuItems = [
    { key: 'user', label: <Text strong>{user?.username}</Text>, disabled: true },
    { key: 'role', label: <Tag color="blue">{user?.role} (Lv.{user?.role_level})</Tag>, disabled: true },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: t('header.logout'), onClick: () => logout('manual') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* 導航選單 */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={items}
          onClick={({ key }) => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            if (key === COLLAPSE_KEY) {
              onCollapse?.();
            } else {
              navigate(key);
              onClose?.();
            }
          }}
          className="sidebar-menu"
          style={{ borderRight: 0 }}
        />
      </div>

      {/* 底部使用者頭像 — Claude 風格 */}
      <div style={{
        borderTop: '1px solid var(--sw-border)', flexShrink: 0,
        padding: collapsed ? '12px 0' : '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 10,
      }}>
        <Dropdown menu={{ items: userMenuItems }} placement="topRight" trigger={['click']}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: 'var(--sw-text)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            fontSize: 16, fontWeight: 700, color: '#FFFFFF',
            userSelect: 'none',
          }}>
            {initial}
          </div>
        </Dropdown>
        {!collapsed && (
          <span style={{
            fontSize: 13, color: 'var(--sw-text)', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user?.username}
          </span>
        )}
      </div>
    </div>
  );
}
